'use client';

import { useState } from 'react';
import {
  Briefcase, Search, Building2, Rocket,
  Activity, Eye, TrendingUp, ArrowUp, ArrowDown,
  Shield, Crown, Cpu, BarChart3,
} from 'lucide-react';
import { useCases } from '@/components/marketing-tivion/marketing-data';

const tabIcons = {
  cmo: Briefcase,
  seo: Search,
  agency: Building2,
  product: Rocket,
};

/* ─── CMO Demo: Executive Market Dashboard ─── */

function CMODemo() {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#121212] overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Briefcase className="h-4 w-4 text-shield-cyan" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
            Executive Dashboard — Rivera Digital
          </span>
        </div>
      </div>

      {/* KPI strip */}
      <div className="px-5 pb-3 grid grid-cols-4 gap-2">
        {[
          { label: 'Market Share', value: '34%', change: '+2.1%', up: true },
          { label: 'Threat Level', value: 'Medium', change: '2 alerts', up: false },
          { label: 'Avg. Position', value: '#4.2', change: '+1.8', up: true },
          { label: 'Active Signals', value: '47', change: 'This week', up: null },
        ].map((kpi) => (
          <div key={kpi.label} className="p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-center">
            <div className="text-[14px] font-bold text-shield-cyan">{kpi.value}</div>
            <div className="text-[7px] text-white/30 uppercase mt-0.5">{kpi.label}</div>
            {kpi.up !== null && (
              <div className={`text-[9px] mt-0.5 font-medium ${kpi.up ? 'text-emerald-400' : 'text-amber-400'}`}>
                {kpi.change}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Competitor threat cards */}
      <div className="mx-5 mb-4 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3.5">
        <div className="text-[9px] font-semibold uppercase tracking-wider text-white/40 mb-2.5">Competitor Activity — Last 7 Days</div>
        <div className="space-y-2">
          {[
            { name: 'NexusPay', signals: 8, threat: 'high', action: 'Pricing pivot + 12 new pages' },
            { name: 'CloudFlow', signals: 4, threat: 'medium', action: 'SERP gains on 5 keywords' },
            { name: 'StreamlineAI', signals: 2, threat: 'low', action: 'Minor visual updates' },
          ].map((comp) => (
            <div key={comp.name} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02]">
              <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                comp.threat === 'high' ? 'bg-red-400' : comp.threat === 'medium' ? 'bg-amber-400' : 'bg-emerald-400'
              }`} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium text-white">{comp.name}</div>
                <div className="text-[9px] text-white/30">{comp.action}</div>
              </div>
              <span className="text-[9px] text-white/40">{comp.signals} signals</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent high-impact signal */}
      <div className="mx-5 mb-5 rounded-lg bg-red-500/[0.06] border border-red-500/15 p-3">
        <div className="flex items-start gap-2">
          <Activity className="h-3.5 w-3.5 text-red-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] font-semibold text-red-400 mb-0.5">High-Priority Alert</div>
            <div className="text-[9px] text-white/50 leading-relaxed">NexusPay dropped enterprise pricing 40% to $299/mo — direct threat to your fintech client.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── SEO Demo: Rankings + Keyword View ─── */

function SEODemo() {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#121212] overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <TrendingUp className="h-4 w-4 text-shield-cyan" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
            SEO Command Center — Fintech Client
          </span>
        </div>
        <div className="flex gap-1">
          {['7d', '30d', '90d'].map((r, i) => (
            <button
              key={r}
              className={`text-[8px] font-semibold px-2 py-0.5 rounded ${
                i === 1 ? 'bg-white/[0.08] text-white' : 'text-white/25'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* SoV mini chart */}
      <div className="mx-5 mb-3 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3.5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[9px] font-semibold uppercase tracking-wider text-white/40">Share of Voice</span>
          <span className="text-[10px] font-bold text-shield-cyan">38%</span>
        </div>
        <div className="h-12 w-full">
          <svg viewBox="0 0 200 40" className="w-full h-full" preserveAspectRatio="none">
            <path d="M0,35 L25,32 L50,28 L75,25 L100,22 L125,20 L150,18 L175,15 L200,12 L200,40 L0,40 Z" fill="rgba(79,209,229,0.1)" />
            <path d="M0,35 L25,32 L50,28 L75,25 L100,22 L125,20 L150,18 L175,15 L200,12" fill="none" stroke="rgba(79,209,229,0.7)" strokeWidth="1.5" />
            <path d="M0,30 L25,31 L50,33 L75,32 L100,34 L125,33 L150,35 L175,36 L200,37" fill="none" stroke="rgba(167,139,250,0.4)" strokeWidth="1" strokeDasharray="3,3" />
          </svg>
        </div>
        <div className="flex items-center gap-4 mt-1.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-shield-cyan" />
            <span className="text-[8px] text-white/40">Client</span>
            <span className="text-[9px] font-bold text-shield-cyan">38%</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-violet-400" />
            <span className="text-[8px] text-white/40">NexusPay</span>
            <span className="text-[9px] font-bold text-violet-400">24%</span>
          </div>
        </div>
      </div>

      {/* Keyword table */}
      <div className="mx-5 mb-5 rounded-lg bg-white/[0.03] border border-white/[0.06] overflow-hidden">
        <div className="grid grid-cols-[1fr_48px_56px_48px] gap-2 px-3 py-2 border-b border-white/[0.06]">
          <span className="text-[8px] font-semibold uppercase tracking-wider text-white/40">Keyword</span>
          <span className="text-[8px] font-semibold uppercase tracking-wider text-white/40 text-center">Rank</span>
          <span className="text-[8px] font-semibold uppercase tracking-wider text-white/40 text-center">Leader</span>
          <span className="text-[8px] font-semibold uppercase tracking-wider text-white/40 text-center">30d</span>
        </div>
        {[
          { kw: 'payment processing API', rank: 2, leader: 'You', isLeader: true, change: 5 },
          { kw: 'fintech platform', rank: 4, leader: 'NP', isLeader: false, change: 3 },
          { kw: 'stripe alternative', rank: 1, leader: 'You', isLeader: true, change: 8 },
          { kw: 'payment gateway SaaS', rank: 6, leader: 'VF', isLeader: false, change: -2 },
        ].map((kw) => (
          <div key={kw.kw} className="grid grid-cols-[1fr_48px_56px_48px] gap-2 px-3 py-2 border-b border-white/[0.04] last:border-0 items-center">
            <span className="text-[11px] text-white truncate">{kw.kw}</span>
            <span className="text-[11px] font-bold text-shield-cyan text-center">#{kw.rank}</span>
            <div className="flex items-center justify-center gap-1">
              {kw.isLeader && <Crown className="h-2.5 w-2.5 text-amber-400" />}
              <span className="text-[9px] text-white/40">{kw.leader}</span>
            </div>
            <div className="flex items-center justify-center gap-0.5">
              {kw.change > 0 ? (
                <>
                  <ArrowUp className="h-2.5 w-2.5 text-emerald-400" />
                  <span className="text-[10px] font-medium text-emerald-400">+{kw.change}</span>
                </>
              ) : (
                <>
                  <ArrowDown className="h-2.5 w-2.5 text-red-400" />
                  <span className="text-[10px] font-medium text-red-400">{kw.change}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Agency Demo: Multi-Client Portfolio ─── */

function AgencyDemo() {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#121212] overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Building2 className="h-4 w-4 text-shield-cyan" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
            Client Portfolio — Rivera Digital
          </span>
        </div>
        <span className="text-[9px] px-2 py-0.5 rounded-full bg-shield-cyan/10 text-shield-cyan border border-shield-cyan/20 font-medium">
          3 clients
        </span>
      </div>

      {/* Client cards */}
      <div className="px-5 pb-4 space-y-2">
        {[
          {
            name: 'Apex Payments',
            domain: 'apexpayments.io',
            signals: 12,
            initials: 'AP',
            color: 'bg-shield-cyan/15 text-shield-cyan',
            health: 'Good',
            healthColor: 'text-emerald-400',
            topKeyword: '"payment processing API" #2',
          },
          {
            name: 'CloudFlow',
            domain: 'cloudflow.com',
            signals: 8,
            initials: 'CF',
            color: 'bg-emerald-500/15 text-emerald-400',
            health: 'At Risk',
            healthColor: 'text-amber-400',
            topKeyword: '"project management" #5 (was #2)',
          },
          {
            name: 'SwiftCart',
            domain: 'swiftcart.com',
            signals: 5,
            initials: 'SC',
            color: 'bg-violet-500/15 text-violet-400',
            health: 'Stable',
            healthColor: 'text-emerald-400',
            topKeyword: '"headless commerce" #3',
          },
        ].map((client) => (
          <div key={client.name} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3.5">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${client.color}`}>
                {client.initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-semibold text-white">{client.name}</span>
                  <span className={`text-[8px] font-medium ${client.healthColor}`}>{client.health}</span>
                </div>
                <div className="text-[9px] text-white/30">{client.domain}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[11px] font-bold text-shield-cyan">{client.signals}</div>
                <div className="text-[7px] text-white/25 uppercase">signals</div>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-white/[0.04] text-[9px] text-white/40">
              Top keyword: <span className="text-white/60">{client.topKeyword}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Summary bar */}
      <div className="mx-5 mb-5 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3 flex items-center justify-between">
        <div className="flex gap-4">
          {[
            { label: 'Total Signals', value: '25', color: 'text-shield-cyan' },
            { label: 'Actionable', value: '8', color: 'text-amber-400' },
            { label: 'Keywords Won', value: '+12', color: 'text-emerald-400' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className={`text-[13px] font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-[7px] text-white/25 uppercase">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Product Demo: Tech Stack + Visual Regression ─── */

function ProductDemo() {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#121212] overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Cpu className="h-4 w-4 text-shield-cyan" />
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
            Tech Intelligence — Competitor Watch
          </span>
        </div>
      </div>

      {/* Tech stack comparison */}
      <div className="mx-5 mb-3 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3.5">
        <div className="text-[9px] font-semibold uppercase tracking-wider text-white/40 mb-2.5">Tech Stack Changes — NexusPay</div>
        <div className="space-y-2">
          <div>
            <div className="text-[8px] text-emerald-400/60 uppercase tracking-wider mb-1">Added</div>
            <div className="flex flex-wrap gap-1.5">
              {['Next.js 15', 'Contentful CMS', 'Segment'].map((tech) => (
                <span key={tech} className="text-[9px] font-medium px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/15">
                  + {tech}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[8px] text-red-400/60 uppercase tracking-wider mb-1">Removed</div>
            <div className="flex flex-wrap gap-1.5">
              {['WordPress', 'HubSpot CMS'].map((tech) => (
                <span key={tech} className="text-[9px] font-medium px-2 py-0.5 rounded-md bg-red-500/10 text-red-300 border border-red-500/15">
                  - {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Visual regression curtain preview */}
      <div className="mx-5 mb-3 rounded-lg bg-white/[0.03] border border-white/[0.06] p-3.5">
        <div className="text-[9px] font-semibold uppercase tracking-wider text-white/40 mb-2.5">Visual Regression — Pricing Page</div>
        <div className="flex rounded-lg overflow-hidden border border-white/[0.06]">
          <div className="flex-1 bg-white/[0.02] p-3 text-center">
            <div className="text-[8px] uppercase tracking-wider text-white/30 mb-1">Before</div>
            <div className="space-y-1">
              <div className="h-2 rounded-full bg-white/[0.08] w-3/4 mx-auto" />
              <div className="text-sm font-bold text-white/60">$499/mo</div>
              <div className="h-4 rounded bg-emerald-500/20 flex items-center justify-center">
                <span className="text-[7px] text-emerald-400 font-bold">Start Trial</span>
              </div>
            </div>
          </div>
          <div className="w-0.5 bg-shield-cyan relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-6 rounded-full bg-shield-cyan flex items-center justify-center">
              <div className="flex gap-px">
                <div className="w-0.5 h-2 rounded-full bg-white/60" />
                <div className="w-0.5 h-2 rounded-full bg-white/60" />
              </div>
            </div>
          </div>
          <div className="flex-1 bg-red-500/[0.03] p-3 text-center">
            <div className="text-[8px] uppercase tracking-wider text-red-400/60 mb-1">After</div>
            <div className="space-y-1">
              <div className="h-2 rounded-full bg-red-500/[0.15] w-3/4 mx-auto" />
              <div className="text-sm font-bold text-red-400">$299/mo</div>
              <div className="h-4 rounded bg-red-500/10 border border-red-500/20 border-dashed flex items-center justify-center">
                <span className="text-[7px] text-red-400 font-bold">Limited Offer</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Signal alert */}
      <div className="mx-5 mb-5 rounded-lg bg-amber-500/[0.06] border border-amber-500/20 p-3">
        <div className="flex items-start gap-2">
          <Eye className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-[10px] font-semibold text-amber-400 mb-0.5">Platform Migration Detected</div>
            <div className="text-[9px] text-white/50 leading-relaxed">NexusPay moved from WordPress to Next.js + Contentful — full headless replatform underway.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Demo Router ─── */

const demoComponents = {
  cmo: CMODemo,
  seo: SEODemo,
  agency: AgencyDemo,
  product: ProductDemo,
};

export function UseCases() {
  const [activeCase, setActiveCase] = useState(useCases[0].id);
  const activeUseCase = useCases.find((uc) => uc.id === activeCase) || useCases[0];
  const Icon = tabIcons[activeUseCase.id] || Briefcase;
  const DemoComponent = demoComponents[activeUseCase.id];

  return (
    <section id="use-cases" className="py-24 lg:py-32 bg-card/50">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground mb-4 text-balance">
            Built for strategic teams
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Intelligence infrastructure that adapts to how your team operates.
          </p>
        </div>

        {/* Tab navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {useCases.map((useCase) => {
            const TabIcon = tabIcons[useCase.id] || Briefcase;
            return (
              <button
                key={useCase.id}
                type="button"
                onClick={() => setActiveCase(useCase.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCase === useCase.id
                    ? 'bg-shield-cyan/10 text-shield-cyan border border-shield-cyan/30'
                    : 'bg-card text-muted-foreground border border-border hover:border-shield-cyan/20 hover:text-foreground'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                {useCase.title}
              </button>
            );
          })}
        </div>

        {/* Active use case content */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left — text content */}
          <div>
            <div className="inline-flex p-3 rounded-lg bg-shield-cyan/10 text-shield-cyan mb-6">
              <Icon className="h-6 w-6" />
            </div>
            <h3 className="text-2xl font-semibold text-foreground mb-2">
              {activeUseCase.title}
            </h3>
            <p className="text-shield-cyan mb-4">{activeUseCase.subtitle}</p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              {activeUseCase.description}
            </p>
            <ul className="space-y-3">
              {activeUseCase.benefits.map((benefit) => (
                <li key={benefit} className="flex items-center gap-3 text-foreground">
                  <div className="w-1.5 h-1.5 rounded-full bg-shield-cyan" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          {/* Right — demo */}
          <div>
            {DemoComponent && <DemoComponent />}
          </div>
        </div>
      </div>
    </section>
  );
}
